import { useMemo, useState } from 'react';
import type { TestSummary } from '../api/types';
import { formatDuration, formatRelative, formatTimestamp } from '../lib/format';
import { PassRateCell } from './PassRateTrend';
import {
  FailureKindPill,
  RunStatusPill,
} from './Pills';
import { ConfirmButton } from './Form';
import { deleteTest } from '../api/endpoints';
import { describeError } from '../api/client';
import { useToast } from '../state';

export type ScenarioBranch = 'positive' | 'negative' | 'edge';

export interface GroupedTest {
  test: TestSummary;
  branch: ScenarioBranch;
}

export interface FeatureGroup {
  id: string;
  name: string;
  tests: GroupedTest[];
  positiveCount: number;
  negativeCount: number;
  edgeCount: number;
  totalPassRate: number | null;
}

/** Detect whether a test case is testing a positive, negative, or edge-case branch. */
export function detectScenarioBranch(test: TestSummary): ScenarioBranch {
  const tags = (test.tags ?? []).map((t) => t.toLowerCase());
  const name = test.name.toLowerCase();
  const desc = (test.description ?? '').toLowerCase();

  if (
    tags.includes('negative') ||
    tags.includes('error') ||
    tags.includes('invalid') ||
    /\b(invalid|wrong|incorrect|fail|error|reject|unauthorized|forbidden|not found|denied|negative)\b/i.test(
      name + ' ' + desc,
    )
  ) {
    return 'negative';
  }

  if (
    tags.includes('edge') ||
    tags.includes('edge-case') ||
    tags.includes('boundary') ||
    /\b(edge|boundary|limit|empty|blank|overflow|exceed|timeout|special characters|null)\b/i.test(
      name + ' ' + desc,
    )
  ) {
    return 'edge';
  }

  return 'positive';
}

/** Group tests by feature prefix or feature tag. */
export function clusterTestsByFeature(tests: readonly TestSummary[]): FeatureGroup[] {
  const map = new Map<string, GroupedTest[]>();

  for (const test of tests) {
    let featureName = 'General';

    // 1. Check for explicit feature:xxx tag
    const featureTag = test.tags?.find((t) => t.toLowerCase().startsWith('feature:'));
    if (featureTag) {
      featureName = featureTag.slice(8).trim();
    } else {
      // 2. Check for Prefix before ':' or '/' or '-' in test.name (e.g. "Auth: Sign in", "Checkout / Payment")
      const prefixMatch = test.name.match(/^([A-Za-z0-9\s_-]+?)(\s*[:/|]\s*)(.*)$/);
      if (prefixMatch && prefixMatch[1] && prefixMatch[1].trim().length > 1) {
        featureName = prefixMatch[1].trim();
      } else if (test.tags && test.tags.length > 0) {
        // 3. Fallback to first non-branch tag
        const tag = test.tags.find(
          (t) => !['positive', 'negative', 'edge', 'smoke', 'regression', 'frontend', 'http'].includes(t.toLowerCase()),
        );
        if (tag) featureName = tag;
      }
    }

    // Capitalize nicely
    featureName = featureName.charAt(0).toUpperCase() + featureName.slice(1);

    const branch = detectScenarioBranch(test);
    const existing = map.get(featureName) ?? [];
    existing.push({ test, branch });
    map.set(featureName, existing);
  }

  const groups: FeatureGroup[] = [];

  for (const [name, items] of map.entries()) {
    let positiveCount = 0;
    let negativeCount = 0;
    let edgeCount = 0;
    let passRateSum = 0;
    let ratedCount = 0;

    for (const item of items) {
      if (item.branch === 'positive') positiveCount++;
      else if (item.branch === 'negative') negativeCount++;
      else if (item.branch === 'edge') edgeCount++;

      if (item.test.passRate !== null) {
        passRateSum += item.test.passRate;
        ratedCount++;
      }
    }

    groups.push({
      id: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      name,
      tests: items,
      positiveCount,
      negativeCount,
      edgeCount,
      totalPassRate: ratedCount > 0 ? passRateSum / ratedCount : null,
    });
  }

  // Sort groups: "General" last, others alphabetical
  return groups.sort((a, b) => {
    if (a.name === 'General') return 1;
    if (b.name === 'General') return -1;
    return a.name.localeCompare(b.name);
  });
}

export interface FeatureGroupedTestsProps {
  tests: readonly TestSummary[];
  pendingId: string | null;
  onSelectTest: (testId: string) => void;
  onRunTest: (test: TestSummary) => void;
  onCheckFlaky: (test: TestSummary) => void;
  onEditPlan: (test: TestSummary) => void;
  onTestDeleted: () => void;
}

export function FeatureGroupedTests({
  tests,
  pendingId,
  onSelectTest,
  onRunTest,
  onCheckFlaky,
  onEditPlan,
  onTestDeleted,
}: FeatureGroupedTestsProps): JSX.Element {
  const toast = useToast();
  const [filterBranch, setFilterBranch] = useState<'all' | ScenarioBranch>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (groupId: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  // 1. Group all tests
  const groups = useMemo(() => clusterTestsByFeature(tests), [tests]);

  // Count totals
  const totalStats = useMemo(() => {
    let pos = 0;
    let neg = 0;
    let edge = 0;
    for (const g of groups) {
      pos += g.positiveCount;
      neg += g.negativeCount;
      edge += g.edgeCount;
    }
    return { pos, neg, edge };
  }, [groups]);

  // Filter groups according to active branch and search query
  const filteredGroups = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return groups
      .map((g) => {
        const filteredTests = g.tests.filter((item) => {
          if (filterBranch !== 'all' && item.branch !== filterBranch) {
            return false;
          }
          if (query) {
            const matchName = item.test.name.toLowerCase().includes(query);
            const matchDesc = (item.test.description ?? '').toLowerCase().includes(query);
            const matchTag = item.test.tags?.some((t) => t.toLowerCase().includes(query));
            return matchName || matchDesc || matchTag;
          }
          return true;
        });

        return { ...g, tests: filteredTests };
      })
      .filter((g) => g.tests.length > 0);
  }, [groups, filterBranch, searchQuery]);

  return (
    <div className="feature-grouped-tests" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Scenario Filters & Search Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          padding: '10px 14px',
          background: 'var(--bg-panel)',
          border: '1px solid var(--border)',
          borderRadius: 8,
        }}
      >
        {/* Scenario Branch Tabs */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-muted)',
              marginRight: 4,
            }}
          >
            Scenario:
          </span>

          <button
            type="button"
            className={`btn btn--small ${filterBranch === 'all' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setFilterBranch('all')}
          >
            All ({tests.length})
          </button>

          <button
            type="button"
            className={`btn btn--small ${filterBranch === 'positive' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setFilterBranch('positive')}
            title="Happy path / success scenarios"
          >
            🟢 Positive ({totalStats.pos})
          </button>

          <button
            type="button"
            className={`btn btn--small ${filterBranch === 'negative' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setFilterBranch('negative')}
            title="Error handling / validation / negative scenarios"
          >
            🔴 Negative ({totalStats.neg})
          </button>

          <button
            type="button"
            className={`btn btn--small ${filterBranch === 'edge' ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => setFilterBranch('edge')}
            title="Boundary and edge case scenarios"
          >
            ⚡ Edge ({totalStats.edge})
          </button>
        </div>

        {/* Search Input */}
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 200 }}>
          <input
            type="search"
            placeholder="Search testcases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              fontSize: 12,
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid var(--border)',
              background: 'var(--bg-inset)',
              color: 'var(--text)',
            }}
          />
        </div>
      </div>

      {filteredGroups.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          No testcases matched filter criteria.
        </div>
      ) : null}

      {/* Grouped Feature Sections */}
      {filteredGroups.map((group) => {
        const isCollapsed = Boolean(collapsedGroups[group.id]);

        return (
          <div
            key={group.id}
            className="feature-group-section"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              overflow: 'hidden',
              background: 'var(--bg-panel)',
            }}
          >
            {/* Group Header */}
            <header
              onClick={() => toggleGroup(group.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 14px',
                background: 'var(--bg-inset)',
                cursor: 'pointer',
                userSelect: 'none',
                borderBottom: isCollapsed ? 'none' : '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                  {isCollapsed ? '▶' : '▼'}
                </span>
                <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>
                  📁 {group.name}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    background: 'rgba(255, 255, 255, 0.08)',
                    padding: '2px 6px',
                    borderRadius: 9999,
                    color: 'var(--text-muted)',
                  }}
                >
                  {group.tests.length} tests
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 6 }}>
                  {group.positiveCount > 0 ? (
                    <span style={{ fontSize: 11, color: '#34d399' }}>
                      {group.positiveCount} pos
                    </span>
                  ) : null}
                  {group.negativeCount > 0 ? (
                    <span style={{ fontSize: 11, color: '#f87171' }}>
                      {group.negativeCount} neg
                    </span>
                  ) : null}
                  {group.edgeCount > 0 ? (
                    <span style={{ fontSize: 11, color: '#fbbf24' }}>
                      {group.edgeCount} edge
                    </span>
                  ) : null}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {isCollapsed ? 'Click to expand' : 'Click to collapse'}
                </span>
              </div>
            </header>

            {/* Testcase Rows inside Group */}
            {!isCollapsed ? (
              <ul className="rows" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {group.tests.map(({ test, branch }) => (
                  <li key={test.id} className="row row--test" style={{ padding: '12px 14px' }}>
                    <div className="row__main">
                      <div className="row__title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {/* Scenario Branch Pill */}
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            padding: '2px 6px',
                            borderRadius: 4,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            background:
                              branch === 'positive'
                                ? 'rgba(16, 185, 129, 0.15)'
                                : branch === 'negative'
                                  ? 'rgba(239, 68, 68, 0.15)'
                                  : 'rgba(245, 158, 11, 0.15)',
                            color:
                              branch === 'positive'
                                ? '#34d399'
                                : branch === 'negative'
                                  ? '#f87171'
                                  : '#fbbf24',
                            border: `1px solid ${
                              branch === 'positive'
                                ? 'rgba(16, 185, 129, 0.3)'
                                : branch === 'negative'
                                  ? 'rgba(239, 68, 68, 0.3)'
                                  : 'rgba(245, 158, 11, 0.3)'
                            }`,
                          }}
                        >
                          {branch === 'positive'
                            ? '🟢 Positive'
                            : branch === 'negative'
                              ? '🔴 Negative'
                              : '⚡ Edge Case'}
                        </span>

                        <button
                          type="button"
                          className="linkish"
                          onClick={() => onSelectTest(test.id)}
                          title="Open Playbook & Runs Workspace"
                          style={{ fontWeight: 600, fontSize: 13.5 }}
                        >
                          {test.name}
                        </button>

                        <span className={`pill pill--${test.type === 'frontend' ? 'info' : 'neutral'} pill--subtle`}>
                          {test.type}
                        </span>

                        {test.healedAt ? (
                          <span className="pill pill--info" title={`Auto-healed ${formatTimestamp(test.healedAt)}`}>
                            healed
                          </span>
                        ) : null}

                        {test.tags.map((tag) => (
                          <span key={tag} className="tag">
                            {tag}
                          </span>
                        ))}
                      </div>

                      <p className="row__meta" style={{ marginTop: 4 }}>
                        {test.latestRun ? (
                          <>
                            <RunStatusPill status={test.latestRun.status} />
                            {test.latestRun.failureKind ? <FailureKindPill kind={test.latestRun.failureKind} /> : null}
                            <span>{formatRelative(test.latestRun.startedAt)}</span>
                            <span className="num">{formatDuration(test.latestRun.durationMs)}</span>
                          </>
                        ) : (
                          <span className="muted">never run</span>
                        )}
                        <span className="row__steps num">{test.planSteps.length} steps</span>
                        {test.stepTimeoutMs ? (
                          <span className="num">step timeout {formatDuration(test.stepTimeoutMs)}</span>
                        ) : null}
                      </p>
                    </div>

                    <div className="row__side">
                      <PassRateCell rate={test.passRate} total={test.totalRuns} />
                      <div className="row__actions">
                        <button
                          type="button"
                          className="btn btn--small btn--secondary"
                          onClick={() => onSelectTest(test.id)}
                          title="Open Playbook flow and in-context runs"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}
                        >
                          <span>⚡</span>
                          <span>Open Playbook</span>
                        </button>
                        <button
                          type="button"
                          className="btn btn--small"
                          disabled={pendingId === test.id}
                          onClick={() => onRunTest(test)}
                        >
                          {pendingId === test.id ? 'Running…' : 'Run'}
                        </button>
                        <button
                          type="button"
                          className="btn btn--small btn--ghost"
                          disabled={pendingId === test.id}
                          onClick={() => onCheckFlaky(test)}
                          title="Run this test three times with healing off and report a stability score"
                        >
                          Flaky check
                        </button>
                        <button type="button" className="btn btn--small btn--ghost" onClick={() => onEditPlan(test)}>
                          Edit plan
                        </button>
                        <ConfirmButton
                          label="Delete"
                          confirmLabel="Really delete?"
                          onConfirm={() => {
                            void deleteTest(test.id)
                              .then(() => {
                                toast.success(`Deleted “${test.name}”`);
                                onTestDeleted();
                              })
                              .catch((error: unknown) => toast.error(describeError(error)));
                          }}
                        />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
